import React from 'react';
import { FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export const LEDGER_ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50] as const;

export const DEFAULT_LEDGER_ROWS_PER_PAGE = 5;

type LedgerRowsPerPageProps = {
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  /** Unique id when multiple ledgers share a page. */
  id?: string;
};

/**
 * Compact rows-per-page control for ledger-style lists (invoices, payouts, commissions).
 */
const LedgerRowsPerPage: React.FC<LedgerRowsPerPageProps> = ({
  value,
  onChange,
  disabled = false,
  id = 'ledger-rows-per-page',
}) => {
  const { t } = useTranslation();

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
      <Typography variant="body2" color="text.secondary" component="label" htmlFor={id}>
        {t('common.pagination.rowsPerPage', 'Rows per page:')}
      </Typography>
      <FormControl size="small" disabled={disabled} sx={{ minWidth: 72 }}>
        <Select
          id={id}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          inputProps={{
            'aria-label': t('common.pagination.rowsPerPage', 'Rows per page:'),
          }}
          sx={{
            fontWeight: 700,
            borderRadius: 1,
            '& .MuiOutlinedInput-notchedOutline': { borderRadius: 1 },
            '& .MuiSelect-select': { py: 0.75, borderRadius: 1 },
          }}
        >
          {LEDGER_ROWS_PER_PAGE_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
};

export default LedgerRowsPerPage;
